import { app } from "../App";
import { getAuth, signOut } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";

const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minut w milisekundach
const WARNING_TIME = 2 * 60 * 1000; // 1 minuta przed wylogowaniem

export const useSessionManager = () => {
  const [timeLeft, setTimeLeft] = useState<number>(SESSION_TIMEOUT);
  const [isWarning, setIsWarning] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  // Resetuj sesję przy aktywności użytkownika
  const resetSession = useCallback(() => {
    setTimeLeft(SESSION_TIMEOUT);
    setIsWarning(false);
    setIsActive(true);
    setLastActivity(Date.now());
  }, []);

  // Obsługa aktywności użytkownika
  const handleActivity = useCallback(() => {
    if (!isActive) return;
    resetSession();
  }, [isActive, resetSession]);

  // Automatyczne wylogowanie
  const logout = useCallback(async () => {
    try {
      await signOut(getAuth(app));
      setIsActive(false);
    } catch (error) {
      console.error("Błąd podczas wylogowania:", error);
    }
  }, []);

  // Timer sesji
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      const remainingTime = SESSION_TIMEOUT - timeSinceLastActivity;

      if (remainingTime <= 0) {
        logout();
        return;
      }

      setTimeLeft(remainingTime);

      // Pokaż ostrzeżenie 2 minuty przed wylogowaniem
      if (remainingTime <= WARNING_TIME && !isWarning) {
        setIsWarning(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, lastActivity, isWarning, logout]);

  // Nasłuchiwanie aktywności użytkownika
  useEffect(() => {
    if (!isActive) return;

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [handleActivity, isActive]);

  // Formatowanie czasu
  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return {
    timeLeft,
    isWarning,
    isActive,
    formatTime,
    resetSession,
    logout,
  };
};
